import type {IVOverride} from '@chekin/api';

export type {IVOverride};

export const IV_LOCKABLE_FIELDS = [
  'name',
  'surname',
  'second_surname',
  'birth_date',
  'gender',
  'document_type',
  'document_number',
] as const satisfies readonly IVOverride['field'][];

export type IVLockableField = (typeof IV_LOCKABLE_FIELDS)[number];

const ISO_DATE_TIME = /^\d{4}-\d{2}-\d{2}T/;

const pad = (n: number) => String(n).padStart(2, '0');

const formatLocalDate = (date: Date) =>
  `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;

export function toComparablePayloadValue(value: unknown): string | null {
  if (value === null || value === undefined) return null;
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : formatLocalDate(value);
  }
  if (typeof value === 'object') {
    return toComparablePayloadValue((value as {value?: unknown}).value);
  }
  const text = String(value).trim();
  if (!text) return null;
  if (typeof value === 'string' && ISO_DATE_TIME.test(text)) {
    const parsed = new Date(text);
    if (!Number.isNaN(parsed.getTime())) return formatLocalDate(parsed);
  }
  return text;
}

const ICAO_TRANSLITERATION: Record<string, string> = {
  ä: 'ae',
  æ: 'ae',
  ö: 'oe',
  ø: 'oe',
  ü: 'ue',
  å: 'aa',
  ß: 'ss',
  ł: 'l',
  đ: 'd',
  ð: 'd',
  þ: 'th',
};

export function normalizeForCompare(value: string | null): string {
  return (value ?? '')
    .normalize('NFC')
    .toLowerCase()
    .replace(/[äæöøüåßłđðþ]/g, char => ICAO_TRANSLITERATION[char])
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .replace(/\s+/g, '');
}

export function getLockedFields(
  scanned: Record<string, unknown> | null | undefined,
): IVLockableField[] {
  if (!scanned) return [];
  return IV_LOCKABLE_FIELDS.filter(
    field => toComparablePayloadValue(scanned[field]) !== null,
  );
}

export function buildIVOverrides(
  scanned: Record<string, unknown>,
  entered: Record<string, unknown>,
  lockedFields: IVLockableField[],
): IVOverride[] {
  const overrides: IVOverride[] = [];
  for (const field of lockedFields) {
    const scannedValue = toComparablePayloadValue(scanned[field]);
    const enteredValue = toComparablePayloadValue(entered[field]);
    if (normalizeForCompare(scannedValue) === normalizeForCompare(enteredValue)) continue;
    overrides.push({field, scanned_value: scannedValue, entered_value: enteredValue});
  }
  return overrides;
}

export const IV_LOCKED_FIELD_ATTRIBUTE = 'data-iv-locked-field';

/**
 * Props for the wrapper of a form field. Disabled inputs fire no click events (Chromium only
 * fires pointerdown), so a locked field's content ignores the pointer and its wrapper gets the
 * clicks instead; IVLockNotice listens for them.
 */
export function getIVLockedFieldProps(lockedFields: readonly string[], field: string) {
  if (!lockedFields.includes(field)) return {};
  return {
    [IV_LOCKED_FIELD_ATTRIBUTE]: true,
    className:
      'cursor-not-allowed select-none [&_*]:pointer-events-none [&_*]:select-none',
  };
}
